import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl, apiConfig } from '../config/api';

interface VisitorMetricsData {
  totalMembers: number;
  leaders: number;
  visitors: number;
  attendees: number;
  members: number;
  engagedMembers: number;
  recentActivity: number;
}

const VisitorMetrics: React.FC = () => {
  const [visitorData, setVisitorData] = useState<VisitorMetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user: _user } = useAuth(); // Prefix with underscore to indicate intentionally unused

  const fetchVisitorMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl(apiConfig.endpoints.analytics.summary));
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to fetch visitor metrics');
      }

      setVisitorData(data.data.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitorMetrics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading visitor metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Visitor Metrics</h1>
        <p className="text-gray-600">Track visitor engagement and growth patterns</p>
      </div>

      {visitorData && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 text-sm font-semibold">👥</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Visitors</p>
                  <p className="text-2xl font-bold text-gray-900">{visitorData.visitors?.toLocaleString() || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-green-600 text-sm font-semibold">🆕</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Recent Activity</p>
                  <p className="text-2xl font-bold text-gray-900">{visitorData.recentActivity?.toLocaleString() || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-purple-600 text-sm font-semibold">🔄</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Members</p>
                  <p className="text-2xl font-bold text-gray-900">{visitorData.totalMembers?.toLocaleString() || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 text-sm font-semibold">📈</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Engagement Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{visitorData.totalMembers > 0 ? ((visitorData.engagedMembers / visitorData.totalMembers) * 100).toFixed(1) : 'N/A'}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Visitor Conversion Funnel</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Total Visitors</span>
                <span className="text-sm font-semibold text-gray-900">{visitorData.visitors?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '100%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Attendees</span>
                <span className="text-sm font-semibold text-gray-900">{visitorData.attendees?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: visitorData.visitors > 0 ? `${(visitorData.attendees / visitorData.visitors) * 100}%` : '0%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Members</span>
                <span className="text-sm font-semibold text-gray-900">{visitorData.members?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full" style={{ width: visitorData.visitors > 0 ? `${(visitorData.members / visitorData.visitors) * 100}%` : '0%' }}></div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Engaged Members</span>
                <span className="text-sm font-semibold text-gray-900">{visitorData.engagedMembers?.toLocaleString() || 'N/A'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-orange-600 h-2 rounded-full" style={{ width: visitorData.visitors > 0 ? `${(visitorData.engagedMembers / visitorData.visitors) * 100}%` : '0%' }}></div>
              </div>
            </div>
          </div>

          {/* Insights */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Visitor Conversion</h3>
                <p className="text-blue-700 text-sm">
                  {visitorData.visitors > 0 ? ((visitorData.attendees / visitorData.visitors) * 100).toFixed(1) : 'N/A'}% of visitors are converting to attendees.
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">Member Engagement</h3>
                <p className="text-green-700 text-sm">
                  {visitorData.totalMembers > 0 ? ((visitorData.engagedMembers / visitorData.totalMembers) * 100).toFixed(1) : 'N/A'}% of total members are highly engaged.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorMetrics; 